/**
 * Control Room: modulene per organisasjon (sak 238, Atle 02.10; migrering 0483).
 *
 * Fem moduler, hver av eller på, i `organization_modules`. Det er den ene sannheten: menyen, rutene,
 * Rettigheter-seksjonene og API-ets «opprett» følger dem. Bare plattformadminen endrer dem
 * (`app.platform_set_organization_modules`), og en ny organisasjon får dem fra invitasjonen.
 *
 * `organizations.kind` er et MERKE siden 0973/0974 (246.6, regel 4: «Modulene er den eneste
 * regelen»). Den UTLEDES av modulene (`kindForModules`) ved opprettelse og ved hver lagring, og ingen
 * atferd spør den — bortsett fra `personal` og `platform`, som er personens eget hus og Tvitvis eget.
 */
import { z } from 'zod';

/*
 * SEKS MODULER SIDEN 0922. `venue` er SPILLESTED (huset har egne saler og arrangementer); `venues` er
 * SPILLESTEDER (253.59): å leie saler hos andre — markedsplassen og stedsregisteret. Før 0922 sto
 * menypunktet for alle festival- og byråhus uten noen modul; migreringen slo den på for dem.
 */
export const ORGANIZATION_MODULES = [
  'festival',
  'venue',
  'venues',
  'concerts',
  'booking',
  'agency',
] as const;
export type OrganizationModule = (typeof ORGANIZATION_MODULES)[number];
export const organizationModuleSchema = z
  .enum(ORGANIZATION_MODULES)
  .meta({ id: 'OrganizationModule' });

/**
 * MODULER SOM TAR MED SEG ANDRE (Atle, 253.58, 9. oktober): «Artister-modulen (Byrå) tar alltid med
 * Konserter-modulen. Konserter kan ikke slås av så lenge Artister er på.» Konserter kan stå alene.
 *
 * Basen håndhever det samme (0922: skranken på `organization_invitations.modules` og avtrekkeren på
 * `organization_modules`), så lista her og den i basen endres sammen.
 */
export const MODULE_REQUIRES: Readonly<
  Partial<Record<OrganizationModule, readonly OrganizationModule[]>>
> = { agency: ['concerts'] };

/** Valget med det modulene tar med seg, i fast rekkefølge. Det API-et lagrer. */
export function withRequiredModules(modules: readonly OrganizationModule[]): OrganizationModule[] {
  const on = new Set<OrganizationModule>(modules);
  for (const module of modules) {
    for (const needed of MODULE_REQUIRES[module] ?? []) on.add(needed);
  }
  return ORGANIZATION_MODULES.filter((module) => on.has(module));
}

/**
 * Modulene som er LÅST PÅ fordi en annen valgt modul tar dem med (Konserter når Artister er valgt).
 * Avkrysningen viser dem huket av og låst, i «Ny organisasjon» og på organisasjonssiden.
 */
export function lockedOnModules(
  modules: readonly OrganizationModule[],
): ReadonlySet<OrganizationModule> {
  const locked = new Set<OrganizationModule>();
  for (const module of modules) {
    for (const needed of MODULE_REQUIRES[module] ?? []) locked.add(needed);
  }
  return locked;
}

/** Minst én, ingen dubletter. Rekkefølgen i svaret er alltid `ORGANIZATION_MODULES`. */
export const organizationModuleListSchema = z
  .array(organizationModuleSchema)
  .min(1)
  .max(ORGANIZATION_MODULES.length)
  .refine((list) => new Set(list).size === list.length, { message: 'duplicate_module' });

/**
 * Typen modulene gir: byrå når byrå er valgt og verken festival, spillested eller booking er det;
 * ellers festival. Samme regel som `app.kind_for_modules` i 0483.
 */
export function kindForModules(modules: readonly OrganizationModule[]): 'festival' | 'agency' {
  const on = new Set(modules);
  return on.has('agency') && !on.has('festival') && !on.has('venue') && !on.has('booking')
    ? 'agency'
    : 'festival';
}

/**
 * Det en ny organisasjon uten data får når ingen har valgt (triggeren i 0483, `derive` på et tomt
 * hus): en festivalorganisasjon har Festival, Konserter (en ren arrangør) og Booking; et byrå har
 * Byrå. Weben bruker den bare når `/v1/me` mangler feltet (en eldre cache eller en testfikstur).
 */
export function defaultModulesForKind(kind: string): readonly OrganizationModule[] {
  /* Spillesteder sto i menyen for begge typene før 0922, og Konserter følger Byrå (253.58). */
  if (kind === 'festival') return ['festival', 'venues', 'concerts', 'booking'];
  if (kind === 'agency') return ['venues', 'concerts', 'agency'];
  return [];
}

/** Sortert i fast rekkefølge, uten dubletter og ukjente verdier. */
export function normalizeModules(modules: readonly string[]): OrganizationModule[] {
  return ORGANIZATION_MODULES.filter((module) => modules.includes(module));
}

/**
 * HVA SOM BLIR STÅENDE NÅR EN MODUL SLÅS AV (260.31, Atle 13.10). Det organisasjonen har, per slag,
 * og modulene som viser hvert slag — de samme som `RESOURCE_MODULES` i policyen gir ressursen, så
 * panelet og `can()` er enige. Et slag blir liggende skjult når ingen av modulene er på lenger;
 * ingenting slettes. Tallene kommer fra `app.platform_organization_module_data` (0974).
 */
export const MODULE_DATA_ITEMS = {
  festivals: ['festival'],
  venues: ['venue'],
  performances: ['festival', 'venue', 'concerts'],
  shifts: ['festival', 'venue', 'concerts'],
  concerts: ['concerts'],
  /* Samme tabell: registeret (Booking) og stallen (Artister). */
  artists: ['booking', 'agency'],
  bids: ['booking', 'concerts'],
  enquiries: ['booking', 'agency'],
  soldGigs: ['agency'],
  agreements: ['agency'],
  dateRequests: ['venues', 'concerts'],
  venueEnquiries: ['venue'],
} as const satisfies Record<string, readonly OrganizationModule[]>;
export type ModuleDataItem = keyof typeof MODULE_DATA_ITEMS;
export const MODULE_DATA_ITEM_KEYS = Object.keys(MODULE_DATA_ITEMS) as [
  ModuleDataItem,
  ...ModuleDataItem[],
];

export const moduleDataCountSchema = z
  .object({ item: z.enum(MODULE_DATA_ITEM_KEYS), count: z.number().int().min(0) })
  .meta({ id: 'ControlRoomModuleDataCount' });
export type ModuleDataCount = z.infer<typeof moduleDataCountSchema>;

/**
 * Slagene med data som er synlige med `before` og blir skjult med `after` — det å slå av modulene
 * faktisk gjør. Et slag en annen påslått modul viser, står (artistene når Booking slås av og
 * Artister er på).
 */
export function hiddenWhenOff(
  data: readonly ModuleDataCount[],
  before: readonly OrganizationModule[],
  after: readonly OrganizationModule[],
): ModuleDataCount[] {
  const shows = (on: readonly OrganizationModule[], item: ModuleDataItem): boolean =>
    MODULE_DATA_ITEMS[item].some((module) => on.includes(module));
  return data.filter((one) => one.count > 0 && shows(before, one.item) && !shows(after, one.item));
}

export const controlRoomOrganizationModulesSchema = z
  .object({
    organizationId: z.uuid(),
    /**
     * Typen er et MERKE (246.6, regel 4): den følger modulene ved hver lagring og styrer ingenting.
     * Før 0974 kunne den låses («Typen kan ikke endres lenger»); det er borte (260.31).
     */
    kind: z.enum(['festival', 'agency', 'personal', 'platform']),
    /** Modulene som er på, i fast rekkefølge. */
    modules: z.array(organizationModuleSchema),
    /** Typen modulene gir. Lik `kind` når modulene er lagret etter 0974. */
    kindForModules: z.enum(['festival', 'agency']),
    /** Det organisasjonen har, per slag (260.31). Tallene blir liggende når en modul slås av. */
    data: z.array(moduleDataCountSchema),
  })
  .meta({ id: 'ControlRoomOrganizationModules' });

export const updateControlRoomOrganizationModulesRequestSchema = z
  .object({ modules: organizationModuleListSchema })
  .meta({ id: 'UpdateControlRoomOrganizationModulesRequest' });

/** Svaret på en lagring: panelet, og om merket (typen) ble et annet. */
export const updateControlRoomOrganizationModulesResponseSchema = z
  .object({
    modules: controlRoomOrganizationModulesSchema,
    kindChanged: z.boolean(),
  })
  .meta({ id: 'UpdateControlRoomOrganizationModulesResponse' });

export type ControlRoomOrganizationModules = z.infer<typeof controlRoomOrganizationModulesSchema>;
export type UpdateControlRoomOrganizationModulesRequest = z.infer<
  typeof updateControlRoomOrganizationModulesRequestSchema
>;
export type UpdateControlRoomOrganizationModulesResponse = z.infer<
  typeof updateControlRoomOrganizationModulesResponseSchema
>;
